import { useCallback, useMemo } from 'react';
import {
  dispatch,
  getSearchKey,
  getSelectedTeamMemberIds,
  getUserData,
  getWorkspaceId,
  TeamMember,
  updateExtraDetails,
  updateSelectedTeamMemberIds,
  useAppSelector,
  useLazyGetTeamSearchQuery,
  UserType,
} from '@zonofi/zono-money-store';
import { usePaginatedList } from './use-paginated-list';
import { useDebounceSearch } from './use-debounce-search';

export const useTeamMembersList = () => {
  const workspaceId = useAppSelector(getWorkspaceId);
  const userData = useAppSelector(getUserData);
  const selectedTeamMemberIds = useAppSelector(getSelectedTeamMemberIds);
  const searchKey = useAppSelector(getSearchKey);
  const [fetchTeamMembers, teamMembersResponse] = useLazyGetTeamSearchQuery();
  const { debouncedSearchValue } = useDebounceSearch();

  const fetchTeamMembersApiCall = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      if (!workspaceId) return;

      const cfaId = userData?.cfaIDs?.[0];

      fetchTeamMembers({
        sellerWorkspaceId: workspaceId,
        page: params.pageNo,
        pageSize: params.pageSize,
        includeInactive: false,
        includePendingInvites: false,
        includeTeamCustomersMapping: true,
        userType: [UserType.SALES],
        ...(cfaId ? { cfaIds: [cfaId] } : {}),
        ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
      });
    },
    [workspaceId, debouncedSearchValue, fetchTeamMembers, userData?.cfaIDs]
  );

  const teamMembersData = useMemo(
    () => teamMembersResponse?.data?.data || [],
    [teamMembersResponse?.data]
  );

  const totalCount = teamMembersResponse?.data?.totalRecords || 0;
  const currentPage = teamMembersResponse?.data?.page || 0;
  const currentPageSize = teamMembersResponse?.data?.pageSize || 0;

  const {
    data,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    isEndOfList,
    refresh,
  } = usePaginatedList<TeamMember>({
    fetchDataApiCall: fetchTeamMembersApiCall,
    apiResponseData: teamMembersData,
    isLoading: teamMembersResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [debouncedSearchValue],
    isError: teamMembersResponse.isError,
    paginationMeta: {
      startRecord:
        totalCount && currentPage && currentPageSize
          ? (currentPage - 1) * currentPageSize + 1
          : 0,
      endRecord:
        totalCount && currentPage && currentPageSize
          ? Math.min(currentPage * currentPageSize, totalCount)
          : 0,
      totalCount,
    },
  });

  const onTeamMemberSelect = (teamMember: TeamMember) => {
    const teamMemberId = teamMember?.id;
    if (!teamMemberId) return;

    const isAlreadySelected = selectedTeamMemberIds?.[0] === teamMemberId;
    dispatch(updateExtraDetails({ teamMemberName: teamMember?.name }));
    dispatch(
      updateSelectedTeamMemberIds(isAlreadySelected ? [] : [teamMemberId])
    );
  };

  return {
    teamMembersData: data,
    lastItemRef,
    isPageLoading,
    onScroll,
    isEndOfList,
    isloading:
      (teamMembersResponse.isLoading || teamMembersResponse?.isFetching) &&
      pageNo === 1,
    searchKey,
    refresh,
    selectedTeamMemberIds,
    onTeamMemberSelect,
  };
};
